import { useEffect, useMemo, useRef, useState } from "react";
import QRCodeStyling, {
  Options as QRCodeStylingOptions,
} from "qr-code-styling";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import PageWrapper from "@/components/PageWrapper.tsx";
import { useGetProfile } from "@/pages/profile/api";
import { useGenerateQR } from "./api";
import BaseButton from "@/components/base/button/BaseButton.tsx";
import VibratingButton from "@/components/base/button/VibratingButton.tsx";
import IconClose from "@/assets/icons/close.svg?react";
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer.tsx";
import IconVoucher from "@/assets/icons/voucher.svg?react";
import IconUsers from "@/assets/icons/users.svg?react";
import IconChevron from "@/assets/icons/chevron.svg?react";
import LoyaltyCard from "./components/LoyaltyCard.tsx";

const QR_REFRESH_SECONDS = 30;

const qrOptions: QRCodeStylingOptions = {
  width: 212,
  height: 212,
  margin: 4,
  type: "svg",
  image: "images/don-logo.svg",
  qrOptions: { typeNumber: 0, mode: "Byte", errorCorrectionLevel: "Q" },
  imageOptions: { imageSize: 0.5, margin: 4, crossOrigin: "anonymous" },
  dotsOptions: { type: "extra-rounded", color: "#000000", roundSize: true },
  backgroundOptions: { round: 0, color: "#ffffff" },
  cornersSquareOptions: { type: "extra-rounded", color: "#000000" },
};

export default function LoyaltyPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: profile, isLoading: isProfileLoading } = useGetProfile();

  const [qrSheetOpen, setQrSheetOpen] = useState(false);
  const [qrReady, setQrReady] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(QR_REFRESH_SECONDS);
  const prevSecondsRef = useRef(QR_REFRESH_SECONDS);
  const justReset = secondsLeft > prevSecondsRef.current;
  useEffect(() => {
    prevSecondsRef.current = secondsLeft;
  });
  const generateQR = useGenerateQR();

  const qrCode = useMemo(() => new QRCodeStyling(qrOptions), []);
  const qrRef = useRef<HTMLDivElement>(null);

  // Mount the QR canvas only after data has been set on qrCode, otherwise
  // an empty / default QR briefly flashes before the real one renders.
  useEffect(() => {
    if (qrSheetOpen && qrReady && qrRef.current) {
      qrRef.current.innerHTML = "";
      qrCode.append(qrRef.current);
    }
  }, [qrSheetOpen, qrReady, qrCode]);

  // 1-second tick driving the visible countdown. Resets on each open.
  useEffect(() => {
    if (!qrSheetOpen) return;
    setSecondsLeft(QR_REFRESH_SECONDS);
    const id = setInterval(() => {
      setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [qrSheetOpen]);

  // Auto-refresh the QR every 30 s while the sheet is open. Silent update
  // (no spinner) so the QR stays visible — only the encoded data swaps.
  // Resets the visible countdown back to 30 on each successful refresh.
  useEffect(() => {
    if (!qrSheetOpen) return;
    const id = setInterval(() => {
      generateQR
        .mutateAsync()
        .then((result) => {
          qrCode.update({ data: result.qr_data });
          setSecondsLeft(QR_REFRESH_SECONDS);
        })
        .catch(() => {});
    }, QR_REFRESH_SECONDS * 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrSheetOpen, qrCode]);

  const handleShowQR = () => {
    setQrReady(false);
    setQrSheetOpen(true);
    generateQR.mutateAsync().then((result) => {
      qrCode.update({ data: result.qr_data });
      setQrReady(true);
    }).catch(() => {
      qrCode.update({ data: `don://loyalty/${profile?.id || "fallback"}` });
      setQrReady(true);
    });
  };

  const walletAmount = profile?.wallet_amount || "0";

  return (
    <PageWrapper backPath="/">
      <div className="container py-3 flex flex-col gap-5">
        {/* DON Loyalty Card */}
        {isProfileLoading ? (
          <div className="rounded-lg bg-white/08 h-[140px] skeleton" />
        ) : (
          <LoyaltyCard
            holderName={profile?.full_name || ""}
            donBalance={walletAmount}
            tier="Standard"
            onShowQR={handleShowQR}
          />
        )}

        {/* Section Cards */}
        <div className="grid grid-cols-2 gap-3">
          <VibratingButton
            onClick={() => navigate("/vouchers")}
            className="flex flex-col items-start gap-3 p-4 rounded-xl bg-white/08 border-b border-white/08 text-start active:bg-white/12 transition-200"
            vibrationType="selection"
          >
            <div className="w-11 h-11 rounded-xl bg-yellow-linear flex-center">
              <IconVoucher className="size-7 text-dark" />
            </div>
            <div className="flex-center-between w-full">
              <p className="text-sm font-semibold text-white leading-130">
                {t("vouchers")}
              </p>
              <IconChevron className="size-5 text-gray-1" strokeWidth="1.5" />
            </div>
          </VibratingButton>

          <VibratingButton
            onClick={() => navigate("/partners")}
            className="flex flex-col items-start gap-3 p-4 rounded-xl bg-white/08 border-b border-white/08 text-start active:bg-white/12 transition-200"
            vibrationType="selection"
          >
            <div className="w-11 h-11 rounded-xl bg-yellow-linear flex-center">
              <IconUsers className="size-6 text-dark" />
            </div>
            <div className="flex-center-between w-full">
              <p className="text-sm font-semibold text-white leading-130">
                {t("our_partners")}
              </p>
              <IconChevron className="size-5 text-gray-1" strokeWidth="1.5" />
            </div>
          </VibratingButton>
        </div>
      </div>

      {/* QR Code Bottom Sheet */}
      <Drawer
        open={qrSheetOpen}
        onOpenChange={(e) => setQrSheetOpen(e)}
        setBackgroundColorOnScale={false}
      >
        <DrawerContent className="bg-gray-4 max-h-[85vh]" showTopThumb>
          <DrawerHeader className="flex justify-between items-center">
            <DrawerTitle className="text-lg">{t("your_qr_code")}</DrawerTitle>
            <DrawerClose>
              <IconClose className="size-6 text-gray-1 cursor-pointer" />
            </DrawerClose>
          </DrawerHeader>
          <DrawerBody className="!pt-0 !pb-safe-bottom">
            <div className="flex flex-col items-center">
              <div className="relative rounded-2xl overflow-hidden mx-auto w-[212px] h-[212px] bg-white">
                <div ref={qrRef} className="w-full h-full" />
                {!qrReady && (
                  <div className="absolute inset-0 flex-center bg-white">
                    <div className="size-12 border-4 border-orange-100/30 border-t-orange-100 rounded-full animate-spin" />
                  </div>
                )}
              </div>
              {/* Countdown ring — full at 30 s, shrinks toward 0,
                  turns red in the last 5 seconds. */}
              <div className="relative size-16 mt-4 flex-center">
                <svg
                  className="absolute inset-0 w-full h-full -rotate-90"
                  viewBox="0 0 36 36"
                >
                  <circle
                    cx="18"
                    cy="18"
                    r="16"
                    fill="none"
                    stroke="rgba(251, 199, 108, 0.18)"
                    strokeWidth="2.5"
                  />
                  <circle
                    cx="18"
                    cy="18"
                    r="16"
                    fill="none"
                    stroke={secondsLeft <= 5 ? "#FF3535" : "#FBC76C"}
                    strokeWidth="2.5"
                    strokeDasharray={2 * Math.PI * 16}
                    strokeDashoffset={
                      2 * Math.PI * 16 * (1 - secondsLeft / QR_REFRESH_SECONDS)
                    }
                    strokeLinecap="round"
                    style={{
                      transition: justReset
                        ? "stroke-dashoffset 0.5s ease-out, stroke 0.3s ease"
                        : "stroke-dashoffset 1s linear, stroke 0.3s ease",
                    }}
                  />
                </svg>
                <span
                  className={`relative font-bold text-base tabular-nums leading-none transition-colors duration-300 ${
                    secondsLeft <= 5 ? "text-red" : "text-orange-100"
                  }`}
                >
                  {secondsLeft}
                </span>
              </div>
              <div className="mt-4 mb-2 rounded-lg bg-white/08 p-3 w-full">
                <p className="text-xs text-gray-1 leading-[160%] text-center">
                  {t("qr_instructions")}
                </p>
              </div>
              <BaseButton
                className="w-full mt-2"
                variant="secondary"
                text={t("close")}
                onClick={() => setQrSheetOpen(false)}
                vibrate
                vibrationType="light"
              />
            </div>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </PageWrapper>
  );
}
